import { useId, useRef, type InputHTMLAttributes, type KeyboardEvent, type ReactNode } from "react";
import { css, cx } from "@strust/ui/css";
import { choice, fieldset, segmented, switchControl } from "@strust/ui/recipes";

export interface CheckboxProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type" | "className"
> {
  readonly label: ReactNode;
  readonly description?: ReactNode;
  readonly className?: string;
}

/** Native checkbox styled as the design's 18 px box. */
export function Checkbox({ label, description, className, ...input }: CheckboxProps) {
  const s = choice({ type: "checkbox" });
  return (
    <label className={cx(s.root, className)}>
      <input {...input} type="checkbox" className={s.input} />
      <span className={css({ display: "flex", flexDirection: "column" })}>
        <span className={s.label}>{label}</span>
        {description ? <span className={s.description}>{description}</span> : null}
      </span>
    </label>
  );
}

export interface RadioProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type" | "className"
> {
  readonly label: ReactNode;
  readonly className?: string;
}

/** Native radio styled as the design's ring with an accent dot. */
export function Radio({ label, className, ...input }: RadioProps) {
  const s = choice({ type: "radio" });
  return (
    <label className={cx(s.root, className)}>
      <input {...input} type="radio" className={s.input} />
      <span className={s.label}>{label}</span>
    </label>
  );
}

export interface RadioGroupProps {
  readonly legend: string;
  readonly name: string;
  readonly value: string;
  readonly options: readonly { readonly value: string; readonly label: ReactNode }[];
  readonly onChange: (value: string) => void;
  readonly orientation?: "vertical" | "horizontal";
  readonly hideLegend?: boolean;
}

/** A fieldset of radios sharing one name. */
export function RadioGroup({
  legend,
  name,
  value,
  options,
  onChange,
  orientation = "vertical",
  hideLegend = false,
}: RadioGroupProps) {
  const s = fieldset();
  return (
    <fieldset className={cx(s.root, css({ border: "none", p: "0" }))}>
      <legend className={hideLegend ? css({ srOnly: true }) : cx(s.legend, css({ px: "0" }))}>
        {legend}
      </legend>
      <div
        className={cx(
          s.body,
          orientation === "horizontal"
            ? css({ flexDirection: "row", flexWrap: "wrap", gap: "5" })
            : undefined,
        )}
      >
        {options.map((option) => (
          <Radio
            key={option.value}
            name={name}
            value={option.value}
            label={option.label}
            checked={option.value === value}
            onChange={() => {
              onChange(option.value);
            }}
          />
        ))}
      </div>
    </fieldset>
  );
}

export interface SwitchProps {
  readonly label: ReactNode;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly disabled?: boolean;
  readonly className?: string;
}

/** On/off switch (`role="switch"`); the track glows when on. */
export function Switch({ label, checked, onChange, disabled, className }: SwitchProps) {
  const s = switchControl();
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      className={cx(s.root, className)}
      onClick={() => {
        onChange(!checked);
      }}
    >
      <span className={s.track} data-part="track" aria-hidden="true">
        <span className={s.thumb} />
      </span>
      <span className={s.label}>{label}</span>
    </button>
  );
}

export interface SegmentedOption {
  readonly value: string;
  readonly label: string;
  readonly icon?: ReactNode;
}

export interface SegmentedControlProps {
  /** Accessible name of the group (from i18n). */
  readonly label: string;
  readonly value: string;
  readonly options: readonly SegmentedOption[];
  readonly onChange: (value: string) => void;
  /** Shows only the icons; each option's label stays its accessible name and tooltip. */
  readonly iconOnly?: boolean;
  readonly className?: string;
}

/**
 * Toggle group (one choice of a few, e.g. Map / Table / Timeline or Dark / Light): a WAI-ARIA
 * radio group with roving focus and arrow-key selection.
 */
export function SegmentedControl({
  label,
  value,
  options,
  onChange,
  iconOnly = false,
  className,
}: SegmentedControlProps) {
  const s = segmented();
  const refs = useRef(new Map<string, HTMLButtonElement>());
  const groupId = useId();

  const move = (event: KeyboardEvent, index: number) => {
    const last = options.length - 1;
    const next =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? index === last
          ? 0
          : index + 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? index === 0
            ? last
            : index - 1
          : null;
    if (next === null) return;
    event.preventDefault();
    const option = options[next];
    if (!option) return;
    onChange(option.value);
    refs.current.get(option.value)?.focus();
  };

  return (
    <div role="radiogroup" aria-label={label} id={groupId} className={cx(s.root, className)}>
      {options.map((option, index) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            ref={(node) => {
              if (node) refs.current.set(option.value, node);
              else refs.current.delete(option.value);
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            title={iconOnly ? option.label : undefined}
            className={cx(s.item, iconOnly ? css({ px: "2.5" }) : undefined)}
            onClick={() => {
              onChange(option.value);
            }}
            onKeyDown={(event) => {
              move(event, index);
            }}
          >
            {option.icon ? (
              <span aria-hidden="true" className={s.icon}>
                {option.icon}
              </span>
            ) : null}
            {iconOnly ? (
              <span className={css({ srOnly: true })}>{option.label}</span>
            ) : (
              option.label
            )}
          </button>
        );
      })}
    </div>
  );
}
